/**
 * Daily nutrient targets, and the one thing a progress bar has to know that a
 * number alone does not carry: which way is good.
 *
 * Protein and fibre are floors — a day at 60 % is a day short. Saturated fat,
 * sugars and salt are ceilings — a day at 60 % is a day going well, and 110 %
 * is the only state worth colouring red. Treating all seven the same is how
 * trackers end up congratulating you for hitting your salt target.
 */

import { round } from './macros.js'

export type NutrientKey = 'protein' | 'carbs' | 'fat' | 'fiber' | 'sugars' | 'satFat' | 'salt'

/** `at_least` = floor, `at_most` = ceiling, `around` = a budget to land near. */
export type TargetDirection = 'at_least' | 'at_most' | 'around'

export const NUTRIENT_DIRECTION: Readonly<Record<NutrientKey, TargetDirection>> = {
  protein: 'at_least',
  carbs: 'around',
  fat: 'around',
  fiber: 'at_least',
  sugars: 'at_most',
  satFat: 'at_most',
  salt: 'at_most',
}

/** Grams, except salt, which labels and targets both state to 0.1 g. */
export const NUTRIENT_DECIMALS: Readonly<Record<NutrientKey, number>> = {
  protein: 0,
  carbs: 0,
  fat: 0,
  fiber: 0,
  sugars: 0,
  satFat: 0,
  salt: 1,
}

export interface NutrientTargets {
  protein: number | null
  carbs: number | null
  fat: number | null
  fiber: number | null
  sugars: number | null
  satFat: number | null
  salt: number | null
}

/**
 * EU Reference Intakes as printed on every label in the shop (Regulation
 * 1169/2011, adult average, 2 000 kcal), plus EFSA's 25 g fibre — which is an
 * absolute recommendation rather than an energy-linked one.
 *
 * Using the label's own numbers is deliberate: the user is comparing the app
 * against the "%RI" column on the packet, and a different basis would make the
 * two disagree for no reason they could see.
 */
export const EU_REFERENCE_INTAKE = {
  kcal: 2000,
  fat: 70,
  satFat: 20,
  carbs: 260,
  sugars: 90,
  protein: 50,
  salt: 6,
  fiber: 25,
} as const

/**
 * Sensible starting limits for a given calorie target.
 *
 * Sugars and saturated fat scale with energy, because both are shares of what
 * you eat. Salt and fibre do not: kidneys and gut bacteria have not read your
 * calorie goal, and EFSA states both as flat daily figures.
 */
export function defaultSecondaryTargets(kcalTarget: number): {
  sugars: number
  satFat: number
  salt: number
  fiber: number
} {
  const scale = kcalTarget > 0 ? kcalTarget / EU_REFERENCE_INTAKE.kcal : 1
  return {
    sugars: Math.round(EU_REFERENCE_INTAKE.sugars * scale),
    satFat: Math.round(EU_REFERENCE_INTAKE.satFat * scale),
    salt: EU_REFERENCE_INTAKE.salt,
    fiber: EU_REFERENCE_INTAKE.fiber,
  }
}

export type TargetStatus = 'none' | 'under' | 'met' | 'over'

export interface NutrientProgress {
  key: NutrientKey
  direction: TargetDirection
  /** Null when the day's foods do not all declare this nutrient. */
  consumed: number | null
  target: number | null
  /** 0..1 for a bar, clamped. Null when there is nothing to compare. */
  fraction: number | null
  status: TargetStatus
  /** Signed: what is left of a ceiling, or still owed on a floor. */
  remaining: number | null
}

/**
 * Grade one nutrient against its target.
 *
 * A ceiling counts as `met` right up to 100 % and only turns `over` past it;
 * a floor is `under` until it is reached. `around` — carbs and fat, which are
 * budgets rather than rules — allows a 10 % overshoot before complaining,
 * because a macro split is an intention, not a limit.
 */
export function nutrientProgress(
  key: NutrientKey,
  consumed: number | null,
  target: number | null,
): NutrientProgress {
  const direction = NUTRIENT_DIRECTION[key]
  const dp = NUTRIENT_DECIMALS[key]

  if (consumed == null || target == null || target <= 0) {
    return {
      key,
      direction,
      consumed: consumed == null ? null : round(consumed, dp),
      target,
      fraction: null,
      status: 'none',
      remaining: null,
    }
  }

  const ratio = consumed / target
  const status: TargetStatus =
    direction === 'at_least'
      ? ratio >= 1
        ? 'met'
        : 'under'
      : direction === 'at_most'
        ? ratio > 1
          ? 'over'
          : 'met'
        : ratio > 1.1
          ? 'over'
          : ratio >= 0.9
            ? 'met'
            : 'under'

  return {
    key,
    direction,
    consumed: round(consumed, dp),
    target,
    fraction: Math.min(1, Math.max(0, ratio)),
    status,
    remaining: round(target - consumed, dp),
  }
}

export interface DayNutrients {
  protein: number | null
  carbs: number | null
  fat: number | null
  fiber: number | null
  sugars: number | null
  satFat: number | null
  salt: number | null
}

export const NUTRIENT_ORDER: readonly NutrientKey[] = [
  'protein',
  'carbs',
  'fat',
  'fiber',
  'sugars',
  'satFat',
  'salt',
]

/** Grade a whole day at once, in the order the UI lists them. */
export function dayNutrientProgress(
  consumed: DayNutrients,
  targets: NutrientTargets,
): NutrientProgress[] {
  return NUTRIENT_ORDER.map((key) => nutrientProgress(key, consumed[key], targets[key]))
}

/** One day of the window, as the streak and adherence figures read it. */
export interface DayOutcome {
  /** Whether anything was written down that day at all. */
  logged: boolean
  /** Whether the day landed inside its own limit. Meaningless when unlogged. */
  withinTarget: boolean
}

/**
 * Consecutive days logged, counting back from the end of the window.
 *
 * Today is allowed to be empty without breaking anything: a streak that resets
 * every midnight and only earns itself back after breakfast would spend most of
 * its life lying. Yesterday empty is a real break.
 */
export function loggingStreak(days: readonly DayOutcome[]): number {
  if (days.length === 0) return 0
  let streak = 0
  const start = days[days.length - 1]?.logged === true ? days.length - 1 : days.length - 2
  for (let i = start; i >= 0; i--) {
    if (days[i]?.logged !== true) break
    streak++
  }
  return streak
}

/**
 * The share of logged days that stayed inside their limit, 0..1.
 *
 * Only logged days count, in both halves of the fraction. Counting a day nobody
 * recorded as a day under the limit would reward not writing things down, which
 * is the one habit a food diary exists to build.
 */
export function adherenceRate(days: readonly DayOutcome[]): number | null {
  const logged = days.filter((d) => d.logged)
  if (logged.length === 0) return null
  return logged.filter((d) => d.withinTarget).length / logged.length
}

export type WeekDayState = 'past' | 'today' | 'future'

export interface WeekDayBudget {
  /** That day's own limit: its calorie target plus whatever it burned. */
  limitKcal: number
  /** What was eaten. Ignored on days that have not happened yet. */
  consumedKcal: number
  state: WeekDayState
}

export interface WeeklyBudget {
  budgetKcal: number
  consumedKcal: number
  /** Signed: negative once the week's whole allowance is spent. */
  remainingKcal: number
  /** Today and the days after it — what the remainder still has to cover. */
  daysLeft: number
  /** The remainder spread evenly over those days. Null when none are left. */
  perDayKcal: number | null
  isOver: boolean
}

/**
 * The week as one budget rather than seven verdicts.
 *
 * A daily limit is a modelling convenience — the body settles up over weeks,
 * not at midnight — but every tracker enforces it as a pass/fail, so a Friday
 * dinner out reads as a failed day even to someone who ate 300 under all week
 * precisely so they could go. That framing is what makes people stop logging
 * the meal, and a diary missing exactly the meals that mattered is worthless.
 *
 * So: sum the limits, sum what has been eaten, and say what is left and how far
 * it has to stretch. Future days contribute their allowance and no intake —
 * that is what makes the figure a budget rather than a running total.
 */
export function weeklyBudget(days: readonly WeekDayBudget[]): WeeklyBudget {
  let budgetKcal = 0
  let consumedKcal = 0
  let daysLeft = 0
  for (const day of days) {
    budgetKcal += day.limitKcal
    if (day.state !== 'future') consumedKcal += day.consumedKcal
    if (day.state !== 'past') daysLeft++
  }
  const remainingKcal = Math.round(budgetKcal - consumedKcal)
  return {
    budgetKcal: Math.round(budgetKcal),
    consumedKcal: Math.round(consumedKcal),
    remainingKcal,
    daysLeft,
    perDayKcal: daysLeft > 0 ? Math.round(remainingKcal / daysLeft) : null,
    isOver: remainingKcal < 0,
  }
}

export interface MealProtein<Slot extends string = string> {
  slot: Slot
  proteinG: number
}

/**
 * The share one meal has to carry before the day counts as lopsided.
 *
 * Not an arbitrary line: muscle protein synthesis responds to a dose — roughly
 * 0,4 g per kg of body weight — and saturates above it, so protein past that
 * point in a single sitting is oxidised rather than used. Three or four such
 * doses is the usual prescription, which puts any one meal at a third or so.
 * Two thirds in one meal means the other meals were below the threshold.
 */
export const PROTEIN_SKEW_SHARE = 0.6

/** Below this the split is not worth a remark — the day was short of protein. */
export const PROTEIN_SKEW_MIN_G = 40

export interface ProteinDistribution<Slot extends string = string> {
  totalG: number
  /** The meal that carried the most of it, when anything was logged. */
  topSlot: Slot | null
  /** That meal's share of the day, 0..1. */
  topShare: number | null
  skewed: boolean
}

/**
 * How a day's protein was spread across its meals.
 *
 * The daily total is the number every tracker shows and the one that hides
 * this: 160 g is a good day if it arrived in four meals and a mediocre one if
 * 120 g of it was dinner. Nothing else in the app can say so, because nothing
 * else looks at the slots the entries were already filed into.
 */
export function proteinDistribution<Slot extends string>(
  meals: readonly MealProtein<Slot>[],
): ProteinDistribution<Slot> {
  const totalG = meals.reduce((sum, meal) => sum + Math.max(0, meal.proteinG), 0)
  if (totalG <= 0) return { totalG: 0, topSlot: null, topShare: null, skewed: false }

  let top = meals[0] as MealProtein<Slot>
  for (const meal of meals) if (meal.proteinG > top.proteinG) top = meal
  const topShare = top.proteinG / totalG
  return {
    totalG: round(totalG, 0),
    topSlot: top.slot,
    topShare,
    skewed: totalG >= PROTEIN_SKEW_MIN_G && topShare > PROTEIN_SKEW_SHARE,
  }
}
